import React, { useState, useEffect } from "react";
import { Clock, FolderOpen } from "lucide-react";
import Avatar from "../Avatar";
import { formatDuration } from "./timeUtils";

/**
 * RunningTaskCard component
 * Displays a single running task in a compact grid card format
 */
const RunningTaskCard = ({ task, onTaskClick }) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(task.elapsedSeconds || 0);

  // Update elapsed time every second
  useEffect(() => {
    if (!task.startedAt) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const started = new Date(task.startedAt).getTime();
      const currentElapsed = Math.floor((now - started) / 1000);
      // totalElapsed = accumulated time from pauses + current session elapsed
      const totalElapsed = (task.accumulatedSeconds || 0) + currentElapsed;
      setElapsedSeconds(totalElapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [task.startedAt, task.accumulatedSeconds]);

  const formattedTime = formatDuration(elapsedSeconds);

  return (
    <div
      onClick={() => {
        if (onTaskClick) {
          onTaskClick(task);
        }
      }}
      className="h-[60px] flex flex-col justify-between px-3 py-1.5 bg-gradient-to-br from-green-50/70 to-emerald-50/70 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800/60 rounded-lg cursor-pointer hover:shadow-md hover:border-green-400 dark:hover:border-green-600 transition-all duration-200"
    >
      {/* Top: Task title and timer */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold text-gray-900 dark:text-slate-200 text-xs truncate flex-1 leading-tight">
          {task.taskTitle}
        </h3>
        <span className="bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 text-[11px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 flex items-center gap-1 font-mono leading-none">
          <Clock className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
          {formattedTime}
        </span>
      </div>

      {/* Bottom: Project info and User avatar + name */}
      <div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-slate-400 leading-tight">
        <div className="flex items-center space-x-1 truncate max-w-[50%]">
          <FolderOpen className="w-3 h-3 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
          <span className="truncate font-medium text-gray-700 dark:text-slate-300">{task.projectName}</span>
          <span className="text-gray-400 font-mono text-[10px]">#{task.cardNumber}</span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Avatar
            user={{ name: task.userName, avatar: task.userAvatar, color: task.userColor }}
            size="xs"
            showTooltip={true}
          />
          <span className="text-[11px] text-emerald-900 dark:text-emerald-200 font-medium truncate max-w-[90px]">
            {task.userName}
          </span>
        </div>
      </div>
    </div>
  );
};

export default RunningTaskCard;
